import { trainingApi } from '@guryou/api-client-gql';

import { execute } from '../../actions/actionWrapper';
import { Action, basicReducer } from '../../core';

// Challenges engine (#1519) — first domain migrated off the training-specific
// trainingQuery()/useTrainingListState() bridge (docs/TRAINING/20_CHALLENGES_ENGINE.md)
// onto the same api-client-gql + Redux flow every proxy-backed domain already uses
// (see ../wallets.js / ../translations.js for the pattern this follows). The other
// Performance Hub domains (fitLibrary catalog, nutrition, fitPlans, coaching/athlete)
// still use trainingQuery as of this writing — same conversion, not yet done.
export const CHALLENGES = { entity: 'TRAINING_CHALLENGES', action: Action.GET_MANY };
export const CHALLENGE = { entity: 'TRAINING_CHALLENGE', action: Action.GET };
export const CHALLENGE_PARTICIPANTS = { entity: 'TRAINING_CHALLENGE_PARTICIPANTS', action: Action.GET_MANY };
export const CHALLENGE_LEADERBOARD = { entity: 'TRAINING_CHALLENGE_LEADERBOARD', action: Action.GET_MANY };
export const CREATE_CHALLENGE = { entity: 'TRAINING_CHALLENGE', action: Action.CREATE };
export const UPDATE_CHALLENGE = { entity: 'TRAINING_CHALLENGE', action: Action.UPDATE };
export const CHALLENGE_STATUS = { entity: 'TRAINING_CHALLENGE_STATUS', action: Action.UPDATE };
export const SET_PARTICIPANT_STATUS = { entity: 'TRAINING_CHALLENGE_PARTICIPANT', action: Action.UPDATE };
export const ERASE_PARTICIPANT = { entity: 'TRAINING_CHALLENGE_PARTICIPANT', action: Action.DELETE };
export const LINKABLE_WORKOUT_PLANS = { entity: 'TRAINING_LINKABLE_WORKOUT_PLANS', action: Action.GET_MANY };
export const LINKABLE_NUTRITION_PLANS = { entity: 'TRAINING_LINKABLE_NUTRITION_PLANS', action: Action.GET_MANY };

export const fetchChallenges =
  (providerId, silent = false) =>
  dispatch =>
    execute(dispatch, () => trainingApi.challengesApiCall({ providerId }), CHALLENGES, null, null, silent);

// silent — the builder form has its own loading state, not a full-page skeleton keyed to this.
export const fetchChallenge = id => dispatch => execute(dispatch, () => trainingApi.challengeApiCall({ id }), CHALLENGE, false, null, true);

export const fetchChallengeParticipants =
  (challengeId, silent = false) =>
  dispatch =>
    execute(dispatch, () => trainingApi.challengeParticipantsApiCall({ challengeId }), CHALLENGE_PARTICIPANTS, null, null, silent);

export const fetchChallengeLeaderboard =
  (challengeId, silent = false) =>
  dispatch =>
    execute(dispatch, () => trainingApi.challengeLeaderboardApiCall({ challengeId }), CHALLENGE_LEADERBOARD, null, null, silent);

export const createChallenge = input => dispatch => execute(dispatch, () => trainingApi.createChallengeApiCall({ input }), CREATE_CHALLENGE);

export const updateChallenge = (id, input) => dispatch =>
  execute(dispatch, () => trainingApi.updateChallengeApiCall({ id, input }), UPDATE_CHALLENGE);

// activate/close/archive/duplicate — every list-page caller does withReload(() => dispatch(one
// of these)) then re-fetches CHALLENGES, so they share one status slot instead of four near-dead
// reducer keys nobody reads directly. silent: true only suppresses the START dispatch — the
// success/error toast from `execute` still fires.
const challengeStatusAction = apiCall => id => dispatch => execute(dispatch, () => apiCall({ id }), CHALLENGE_STATUS, null, null, true);
export const activateChallenge = challengeStatusAction(trainingApi.activateChallengeApiCall);
export const closeChallenge = challengeStatusAction(trainingApi.closeChallengeApiCall);
export const archiveChallenge = challengeStatusAction(trainingApi.archiveChallengeApiCall);
export const duplicateChallenge = challengeStatusAction(trainingApi.duplicateChallengeApiCall);

export const setParticipantStatus = (athleteChallengeId, status) => dispatch =>
  execute(dispatch, () => trainingApi.setParticipantStatusApiCall({ athleteChallengeId, status }), SET_PARTICIPANT_STATUS);

export const eraseParticipant = athleteChallengeId => dispatch =>
  execute(dispatch, () => trainingApi.eraseParticipantApiCall({ athleteChallengeId }), ERASE_PARTICIPANT);

// "Link a training/nutrition plan" pickers in the builder — only public plans are
// selectable (039 / #1519 follow-up, see challenges/api.js's original comment).
export const fetchLinkableWorkoutPlans = providerId => dispatch =>
  execute(dispatch, () => trainingApi.workoutPlansApiCall({ providerId }), LINKABLE_WORKOUT_PLANS, false, null, true);

export const fetchLinkableNutritionPlans = providerId => dispatch =>
  execute(dispatch, () => trainingApi.nutritionPlansApiCall({ providerId }), LINKABLE_NUTRITION_PLANS, false, null, true);

export const challengesReducers = {
  list: basicReducer(CHALLENGES),
  detail: basicReducer(CHALLENGE),
  participants: basicReducer(CHALLENGE_PARTICIPANTS),
  leaderboard: basicReducer(CHALLENGE_LEADERBOARD),
  create: basicReducer(CREATE_CHALLENGE),
  update: basicReducer(UPDATE_CHALLENGE),
  status: basicReducer(CHALLENGE_STATUS),
  participantStatus: basicReducer(SET_PARTICIPANT_STATUS),
  eraseParticipant: basicReducer(ERASE_PARTICIPANT),
  linkableWorkoutPlans: basicReducer(LINKABLE_WORKOUT_PLANS, raw => (raw?.data || []).filter(p => p.public)),
  linkableNutritionPlans: basicReducer(LINKABLE_NUTRITION_PLANS),
};
